/**
 * Content sanitization utilities for message formatting
 */

/**
 * Tags that keep their structure when a message is rendered. Anything else is
 * unwrapped so its contents - including links - survive.
 */
const ALLOWED_TAGS = [
  'p', 'br', 'div', 'span',           // Basic structure
  'strong', 'b',                       // Bold
  'em', 'i',                          // Italic/emphasis
  'u',                                // Underline
  'ul', 'ol', 'li',                   // Lists
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', // Headers
  'blockquote',                       // Quotes
  'pre', 'code',                      // Code blocks
  'a',                                // Links
  'img',                              // Images
];

/**
 * Tags removed together with their content, because their content is never
 * something we want to show or run inside a message bubble.
 */
const DANGEROUS_TAGS = [
  'script', 'style', 'iframe', 'object', 'embed', 'noscript', 'template',
  'form', 'input', 'button', 'select', 'textarea', 'link', 'meta', 'base',
];

/** Attributes kept per tag. Every other attribute is dropped. */
const ALLOWED_ATTRIBUTES: { [tagName: string]: string[] } = {
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt'],
};

/**
 * Resolves a link target that is safe to render inside a message.
 *
 * Editors and pasted email content regularly produce hrefs without a scheme
 * ("www.example.com"), which used to be dropped and left the anchor unclickable.
 * @param raw - The raw href value
 * @returns A safe absolute href, or null when the link should be dropped
 */
function safeLinkHref(raw: string | null): string | null {
  if (!raw) {
    return null;
  }

  const value = raw.trim();

  if (!value || value.charAt(0) === '#' || value.charAt(0) === '/') {
    return null;
  }

  if (/^(?:https?|mailto|tel):/i.test(value)) {
    return value;
  }

  // Any other explicit scheme (javascript:, data:, file:, ...) is not rendered.
  if (/^[A-Za-z][A-Za-z0-9+.\-]*:/.test(value)) {
    return null;
  }

  return 'https://' + value;
}

/**
 * Resolves an image source that is safe to render inside a message.
 * @param raw - The raw src value
 * @returns A safe src, or null when the image should be dropped
 */
function safeImageSrc(raw: string | null): string | null {
  if (!raw) {
    return null;
  }

  const value = raw.trim();

  if (!value) {
    return null;
  }

  if (/^data:image\//i.test(value) || /^(?:https?:)?\/\//i.test(value)) {
    return value;
  }

  if (/^[A-Za-z][A-Za-z0-9+.\-]*:/.test(value)) {
    return null;
  }

  return value;
}

/**
 * Replaces an element with its own children.
 * @param element - The element to unwrap
 */
function unwrap(element: Element): void {
  const parent = element.parentNode;

  if (!parent) {
    return;
  }

  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }

  parent.removeChild(element);
}

/**
 * Strips every attribute that is not explicitly allowed for the tag and
 * normalises link and image targets.
 * @param element - The element to clean
 * @param tagName - The lower cased tag name
 */
function cleanAttributes(element: Element, tagName: string): void {
  const allowed = ALLOWED_ATTRIBUTES[tagName] || [];

  Array.from(element.attributes).forEach((attribute) => {
    if (allowed.indexOf(attribute.name.toLowerCase()) === -1) {
      element.removeAttribute(attribute.name);
    }
  });

  if (tagName === 'a') {
    const href = safeLinkHref(element.getAttribute('href'));

    element.removeAttribute('href');
    element.removeAttribute('target');
    element.removeAttribute('rel');

    if (href) {
      element.setAttribute('href', href);

      if (!/^(?:mailto|tel):/i.test(href)) {
        element.setAttribute('target', '_blank');
        element.setAttribute('rel', 'noopener noreferrer');
      }
    }

    return;
  }

  if (tagName === 'img') {
    const src = safeImageSrc(element.getAttribute('src'));

    element.removeAttribute('src');

    if (src) {
      element.setAttribute('src', src);
    }
  }
}

/**
 * Sanitizes HTML content by removing all inline styles and unsupported tags
 * while preserving structural formatting and links
 * @param html - The HTML content to sanitize
 * @returns Sanitized HTML content with only allowed HTML tags
 */
export function sanitizeMessageContent(html: string): string {
  if (!html) {
    return html;
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  DANGEROUS_TAGS.forEach((tagName) => {
    Array.from(container.getElementsByTagName(tagName)).forEach((element) => {
      const parent = element.parentNode;

      if (parent) {
        parent.removeChild(element);
      }
    });
  });

  // Snapshot in document order: unwrapping moves children up but leaves them in
  // the tree, so they are still visited later in this same pass.
  Array.from(container.querySelectorAll('*')).forEach((element) => {
    const tagName = element.tagName.toLowerCase();

    if (ALLOWED_TAGS.indexOf(tagName) === -1) {
      unwrap(element);
      return;
    }

    cleanAttributes(element, tagName);
  });

  return container.innerHTML;
}

/**
 * Complete content sanitization for chat messages
 * @param content - The message content to sanitize
 * @returns Sanitized content safe for display in message bubbles
 */
export function sanitizeForMessageBubble(content: string): string {
  if (!content) {
    return content;
  }

  return sanitizeMessageContent(content);
}

/**
 * Flattens message content to a single line of readable text, for previews that
 * truncate the message and therefore cannot render markup safely.
 * @param content - The message content to flatten
 * @returns The message text without any markup
 */
export function messageContentToPlainText(content: string): string {
  if (!content) {
    return '';
  }

  const container = document.createElement('div');
  container.innerHTML = content;

  Array.from(container.querySelectorAll('script, style, noscript, template')).forEach((element) => {
    const parent = element.parentNode;

    if (parent) {
      parent.removeChild(element);
    }
  });

  Array.from(container.querySelectorAll('br')).forEach((element) => {
    const parent = element.parentNode;

    if (parent) {
      parent.insertBefore(document.createTextNode(' '), element);
    }
  });

  Array.from(container.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote')).forEach((element) => {
    element.appendChild(document.createTextNode(' '));
  });

  return (container.textContent || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
